import CryptoJS from 'crypto-js';
import JSEncrypt from 'jsencrypt';
import { getFlagD, getRSAPuKey } from "@/utils/auth";

/**
 * 生成AES256密钥（通过SHA256哈希）
 * @param {string} seed - 密钥种子（如密码、随机字符串）
 * @returns {CryptoJS.lib.WordArray} 32字节的AES256密钥
 */
export function generateAESKey(seed) {
  // SHA256哈希结果为32字节
  return CryptoJS.SHA256(seed);
}

/**
 * AES256-ECB加密（SHA256生成密钥，输出Base16格式）
 * @param {string} data - 待加密数据
 * @param {string} seed - 密钥种子（用于生成AES密钥）
 * @returns {string} 加密后的Base16（十六进制）字符串
 */
export function aes256Encrypt(data, seed) {
  // 生成32字节AES256密钥
  const key = generateAESKey(seed);
  
  // 执行AES256-ECB加密
  const encrypted = CryptoJS.AES.encrypt(data, key, {
    mode: CryptoJS.mode.ECB, // ECB模式（无IV）
    padding: CryptoJS.pad.Pkcs7 // 标准填充方式
  });
  
  // 将加密结果转换为Base16（十六进制）格式
  return encrypted.ciphertext.toString(CryptoJS.enc.Hex);
}


/**
 * AES256-ECB解密（对应加密方法，输入Base16格式）
 * @param {string} encryptedData - Base16格式的加密数据
 * @param {string} seed - 密钥种子（需与加密时一致）
 * @returns {string} 解密后的原始数据
 */
export function aes256Decrypt(encryptedData, seed) {
  // 生成与加密时相同的密钥
  const key = generateAESKey(seed);
  
  // 将Base16字符串转换为二进制数据（WordArray）
  const ciphertext = CryptoJS.enc.Hex.parse(encryptedData);
  
  // 执行解密
  const decrypted = CryptoJS.AES.decrypt({ ciphertext }, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  });

  // console.log(decrypted.toString());
  
  // 转换为UTF8明文
  return decrypted.toString(CryptoJS.enc.Utf8);
}

/**
 * 生成临时AES-256密钥（32字节随机二进制）
 * @returns {Object} 包含原始密钥和不同格式的对象
 */
function generateAes256Key() {
  // 生成32字节（256位）的随机二进制数据（WordArray格式）
  const randomKey = CryptoJS.lib.WordArray.random(32);
  
  return {
    raw: randomKey, // CryptoJS专用的WordArray格式（可直接用于CryptoJS加密）
    hex: randomKey.toString(CryptoJS.enc.Hex), // 十六进制格式（64字符）
    base64: randomKey.toString(CryptoJS.enc.Base64) // Base64格式（约44字符）
  };
}



/**
 * 格式化公钥为标准PEM格式
 * @param {string} rawKey - 接口返回的纯字符串公钥（无头部尾部）
 * @returns {string} 完整的PEM格式公钥
 */
function formatPublicKey(rawKey) {
  // 移除可能存在的空格和换行
  const cleanedKey = rawKey.replace(/\s/g, '');
  
  // 拼接标准PEM格式头尾部
  return `-----BEGIN PUBLIC KEY-----\n${cleanedKey}\n-----END PUBLIC KEY-----`;
}


/**
 * 登录流程！！  RSA加密并输出Base16（十六进制）格式
 * @param {string} data - 待加密数据
 * @param {string} rawKey - 接口返回的纯字符串公钥（无头部尾部）
 * @returns {string|null} 加密结果
 */
export function rsaEncrypt(data,rawKey) {
  //登录流程加密
  // 格式化公钥并缓存
  if (!rawKey) {
    // console.error('公钥未初始化');
    return null;
  }
  let formattedPublicKey = formatPublicKey(rawKey);

  // console.log(formattedPublicKey);

  const encryptor = new JSEncrypt();
  // 初始化公钥 设置格式化后的PEM公钥
  encryptor.setPublicKey(formattedPublicKey);

  // RSA加密默认返回Base64格式
  const encryptedBase64 = encryptor.encrypt(data);

  // 将Base64转换为Base16（十六进制）
  const wordArray = CryptoJS.enc.Base64.parse(encryptedBase64);
  return wordArray.toString(CryptoJS.enc.Hex);
}

/**
 * 控制指令RSA加密并输出Base16（十六进制）格式
 * @param {string} confirmText - 待加密数据
 * @returns {string|null} 加密结果
 */
export function controlEncrypt(confirmText){
  let key = getRSAPuKey();
  let flagD = getFlagD();
  // let H2 = CryptoJS.SHA256(JSON.stringify(confirmText)).toString(CryptoJS.enc.Hex);
  let H2 = generateAESKey(JSON.stringify(confirmText)).toString(CryptoJS.enc.Hex);
  console.log(H2);
  let D1 = JSON.stringify(confirmText) + H2;
  let A2 = generateAes256Key();

  // 执行AES256-ECB加密
  const encrypted = CryptoJS.AES.encrypt(D1, A2.raw, {
    mode: CryptoJS.mode.ECB, // ECB模式（无IV）
    padding: CryptoJS.pad.Pkcs7 // 标准填充方式
  });
  
  // 参数加密 十六进制输出
  let encryptedPara = encrypted.ciphertext.toString(CryptoJS.enc.Hex);

  let formattedPublicKey = formatPublicKey(key);
  const encryptor = new JSEncrypt();
  // 初始化公钥 设置格式化后的PEM公钥
  encryptor.setPublicKey(formattedPublicKey);

  // RSA加密默认返回Base64格式
  const encryptedBase64 = encryptor.encrypt(A2.base64);

  // 将Base64转换为Base16（十六进制）
  const wordArray = CryptoJS.enc.Base64.parse(encryptedBase64);

  //A2加密 十六进制输出
  let ciphertextAES = wordArray.toString(CryptoJS.enc.Hex);

  return {
    encryptedPara,ciphertextAES,flagD}
}